import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTicketProjectTasks, getProjects, getTicketProjects, createProjectTask, errorMessage } from '../services/api';
import { canAction } from '../menu';
import { useLanguage } from '../i18n/LanguageContext';
import Select from './Select';
import { NtIcon } from './NotionUI';

/**
 * Riquadro nella pagina del ticket: le attività dei Progetti collegate a
 * questo ticket e, per chi può gestire i progetti, la creazione di una nuova
 * attività (già collegata). Se la coda del ticket ha un progetto collegato, lo si propone in evidenza
 * con un solo clic; resta possibile aggiungere l'attività anche ad altri progetti.
 */
function TicketProjectTasks({ ticket, user }) {
  const { t } = useLanguage();
  const canManageProjects = canAction(user, 'projects.manage');
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [queueProject, setQueueProject] = useState(null); // progetto collegato alla coda del ticket
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getTicketProjectTasks(ticket.id)
      .then((data) => setTasks(data.tasks || []))
      .catch(() => {});
    if (canManageProjects) {
      getProjects()
        .then((data) => setProjects(data.projects || []))
        .catch(() => {});
      getTicketProjects()
        .then((data) => {
          const queue = (data.projects || []).find((q) => q.id === ticket.project_id);
          setQueueProject(queue?.linked_project || null);
        })
        .catch(() => {});
    }
  }, [ticket.id, ticket.project_id, canManageProjects]);

  const addTo = async (project) => {
    setBusy(true);
    setError('');
    try {
      const data = await createProjectTask(Number(project.id), { title: ticket.subject, ticket_id: ticket.id });
      setTasks((prev) => [...prev, {
        task_id: data.task.id,
        title: data.task.title,
        project_id: project.id,
        project_name: project.name,
        project_icon: project.icon,
      }]);
      setProjectId('');
    } catch (err) {
      setError(errorMessage(err, t('tickets.projectTaskError')));
    } finally {
      setBusy(false);
    }
  };

  const createTask = () => {
    const project = projects.find((p) => p.id === Number(projectId));
    if (project) addTo(project);
  };

  if (tasks.length === 0 && !canManageProjects) return null;

  const alreadyInQueueProject = queueProject && tasks.some((task) => task.project_id === queueProject.id);
  // Nell'elenco degli altri progetti non si ripropone quello della coda (ha già il suo pulsante).
  const otherProjects = projects.filter((p) => !queueProject || p.id !== queueProject.id);

  return (
    <div className="card ticket-linked-tasks">
      <div className="card-body">
        <label className="sidebar-field-label">{t('tickets.projectTasks')}</label>
        {tasks.length === 0 && <p className="nt-muted">{t('tickets.noProjectTasks')}</p>}
        {tasks.map((task) => (
          <div key={task.task_id} className="pd-link-item tpt-row">
            <span aria-hidden="true">{task.project_icon || '📋'}</span>
            <Link to={`/projects/${task.project_id}?task=${task.task_id}`} state={{ from: `/tickets/${ticket.id}` }} title={task.project_name}>
              {task.title || t('projectDetail.untitled')}
            </Link>
            {queueProject && task.project_id === queueProject.id && <span className="tpt-badge">{t('tickets.queueProjectBadge')}</span>}
            {task.done ? <span className="tpt-done" title={task.status_label || ''}>✓ {t('tickets.projectTaskDone')}</span> : task.status_label && <span className="nt-muted">{task.status_label}</span>}
          </div>
        ))}

        {canManageProjects && queueProject && !alreadyInQueueProject && (
          <div className="tpt-queue">
            <div className="tpt-queue-info">
              <span className="tpt-queue-icon" aria-hidden="true">{queueProject.icon || '📋'}</span>
              <div>
                <strong>{queueProject.name}</strong>
                <small>{t('tickets.queueProjectHint')}</small>
              </div>
            </div>
            <button type="button" className="btn tpt-queue-btn" disabled={busy} onClick={() => addTo(queueProject)}>
              <NtIcon name="plus" size={15} /> {t('tickets.addToQueueProject')}
            </button>
          </div>
        )}

        {canManageProjects && otherProjects.length > 0 && (
          <div className="ticket-linked-tasks-create">
            {queueProject && <p className="tpt-other">{t('tickets.otherProject')}</p>}
            <div className="tpt-pick">
              <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label={t('tickets.projectTaskProject')}>
                <option value="">{t('tickets.projectTaskProject')}</option>
                {otherProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.icon ? `${p.icon} ${p.name}` : p.name}</option>
                ))}
              </Select>
              <button type="button" className="btn btn-secondary" disabled={!projectId || busy} onClick={createTask}>
                <NtIcon name="plus" size={15} /> {t('tickets.createProjectTask')}
              </button>
            </div>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
}

export default TicketProjectTasks;
